import { Fragment, type ReactNode } from "react";

/**
 * Formatação inline no estilo WhatsApp: `*negrito*`, `_itálico_` e `~tachado~`.
 * URLs (`http(s)://` e `www.`) viram links que abrem em nova aba.
 *
 * Regras (as mesmas do WhatsApp) para não formatar texto que só usa esses
 * caracteres literalmente, como `snake_case`, `2 * 3` ou `arquivo_final.pdf`:
 * - o marcador de abertura não pode vir depois de letra/número;
 * - não pode haver espaço depois da abertura nem antes do fechamento;
 * - o marcador de fechamento não pode vir antes de letra/número;
 * - a formatação não atravessa quebra de linha.
 *
 * Marcadores diferentes podem ser combinados (`*_negrito e itálico_*`).
 */

type Marker = "*" | "_" | "~";

const WRAPPERS: Record<Marker, (children: ReactNode, key: string) => ReactNode> = {
  "*": (children, key) => (
    <strong key={key} className="font-semibold">
      {children}
    </strong>
  ),
  "_": (children, key) => (
    <em key={key} className="italic">
      {children}
    </em>
  ),
  "~": (children, key) => (
    <s key={key} className="line-through">
      {children}
    </s>
  ),
};

const ANY_MARKER = /[*_~]/;
const WORD_CHAR = /[\p{L}\p{N}]/u;
const WHITESPACE = /\s/;
const HAS_URL = /https?:\/\/\S+|www\.\S+/i;
/** Captura URLs; pontuação final comum (.,;:!?) é removida depois. */
const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_URL_PUNCT = /[.,;:!?)]+$/;

const isMarker = (char: string | undefined): char is Marker =>
  char === "*" || char === "_" || char === "~";

function canOpen(text: string, index: number): boolean {
  const marker = text[index];
  const before = text[index - 1];
  const after = text[index + 1];

  if (after === undefined || WHITESPACE.test(after) || after === marker) return false;
  if (before !== undefined && (WORD_CHAR.test(before) || before === marker)) return false;

  return true;
}

/** Índice do marcador que fecha o trecho, ou -1 se não houver um válido na mesma linha. */
function findClosing(text: string, marker: Marker, from: number): number {
  for (let i = from; i < text.length; i++) {
    const char = text[i];

    if (char === "\n") return -1;
    if (char !== marker) continue;
    if (WHITESPACE.test(text[i - 1])) continue;

    const after = text[i + 1];
    if (after !== undefined && WORD_CHAR.test(after)) continue;

    return i;
  }

  return -1;
}

function hrefFor(rawUrl: string): string | null {
  const trimmed = rawUrl.replace(TRAILING_URL_PUNCT, "");
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  return null;
}

function linkifyPlain(text: string, keyPrefix: string): ReactNode[] {
  if (!HAS_URL.test(text)) {
    return [<Fragment key={`${keyPrefix}t0`}>{text}</Fragment>];
  }

  // Reset lastIndex on the global URL matcher before scanning
  URL_RE.lastIndex = 0;

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let matchIndex = 0;

  while ((match = URL_RE.exec(text)) !== null) {
    const raw = match[0];
    const trailing = raw.match(TRAILING_URL_PUNCT)?.[0] ?? "";
    const core = trailing ? raw.slice(0, -trailing.length) : raw;
    const href = hrefFor(core);

    if (match.index > lastIndex) {
      nodes.push(
        <Fragment key={`${keyPrefix}t${lastIndex}`}>
          {text.slice(lastIndex, match.index)}
        </Fragment>
      );
    }

    if (href && core) {
      nodes.push(
        <a
          key={`${keyPrefix}a${matchIndex}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline break-all hover:opacity-80"
        >
          {core}
        </a>
      );
      if (trailing) {
        nodes.push(
          <Fragment key={`${keyPrefix}p${matchIndex}`}>{trailing}</Fragment>
        );
      }
    } else {
      nodes.push(
        <Fragment key={`${keyPrefix}t${match.index}`}>{raw}</Fragment>
      );
    }

    lastIndex = match.index + raw.length;
    matchIndex++;
  }

  if (lastIndex < text.length) {
    nodes.push(
      <Fragment key={`${keyPrefix}t${lastIndex}`}>{text.slice(lastIndex)}</Fragment>
    );
  }

  return nodes;
}

function parse(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let plainStart = 0;
  let index = 0;

  const pushPlain = (end: number) => {
    if (end > plainStart) {
      nodes.push(...linkifyPlain(text.slice(plainStart, end), `${keyPrefix}${plainStart}-`));
    }
  };

  while (index < text.length) {
    const char = text[index];

    if (isMarker(char) && canOpen(text, index)) {
      const closing = findClosing(text, char, index + 1);

      if (closing !== -1) {
        pushPlain(index);
        const inner = text.slice(index + 1, closing);
        nodes.push(
          WRAPPERS[char](parse(inner, `${keyPrefix}${index}-`), `${keyPrefix}m${index}`)
        );
        index = closing + 1;
        plainStart = index;
        continue;
      }
    }

    index++;
  }

  pushPlain(text.length);
  return nodes;
}

export function formatWhatsAppText(text: string | null | undefined): ReactNode {
  if (!text) return text ?? "";
  if (!ANY_MARKER.test(text)) {
    return HAS_URL.test(text) ? linkifyPlain(text, "") : text;
  }

  return parse(text, "");
}
